import styles from "./SpiderLibrary.module.css";
import { useCallback, useEffect, useState, useRef } from "react";
import { Spinner, Button, Modal } from "react-bootstrap";
import AGTable from "../../components/AGTable/AGTable";
import TimedAlert from "../../components/TimedAlert/TimedAlert";
import { Form } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { toTitleCase } from "../../utilities/functions";
import SpiderScheduleForm from "../SpiderScheduling/SpiderScheduleForm/SpiderScheduleForm";
import { getSpiderLibrary, updatePortalSchedule, updateSpiderLibrary } from "../../api/portalApi";

const SpiderLibrary = (props) => {
    const location = useLocation();

    // Data States
    const gridRef = useRef();
    const [spiderLibraryData, setSpiderLibraryData] = useState();
    const [updatedRow, setUpdatedRow] = useState();
    const [error, setError] = useState(false);
    const [spiderFilter, setSpiderFilter] = useState('');
    const [detailedObj, setDetailedObj] = useState();
    const [detailedPage, setDetailedPage] = useState("detailed");
    const [scheduleObj, setScheduleObj] = useState({
        client: "",
        dayOfWeek: [{value: "Every day", label: "Every day"}],
        day: [{value: "Every day", label: "Every day"}],
        hour: "00:00",
        minute: "00",
        batch_size: 20,
        batch_time: 5,
        spiderArgs: [{
            bucket_export: 1
        }]
    });

    // UI States
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState();
    const [showAlert, setShowAlert] = useState(false);
    const [showDetailedModal, setShowDetailedModal] = useState(false);

    const fetchData = useCallback( async() => {
        setLoading(true);
        try {
            const res = await getSpiderLibrary();
            setSpiderLibraryData(res?.data?.data || res?.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
        
    }, []);
    
    const postUpdatedRow = useCallback(async() => {
        const excludeKey = "detailed";
        const filteredUpdatedRow = Object.fromEntries(
            Object.entries(updatedRow).filter(([key]) => key !== excludeKey)
        );

        try {
            await updateSpiderLibrary(filteredUpdatedRow);
            setAlert("Data Updated Successfully");
        } catch (e) {
            console.error(e);
            setError(e);
            setAlert("Error in updating data");
        } finally {
            setShowAlert(true)
        }

    }, [updatedRow]);
    
    useEffect(() => { 
        fetchData();
    }, [fetchData]);
    
    useEffect((loading) => {
        if (updatedRow){
            const runAsync = async () => {
                setLoading(true);
                await postUpdatedRow(loading);
                await setSpiderLibraryData();
                await fetchData();
            }

            runAsync();
        }
        // setLoading(false);
    }, [updatedRow, postUpdatedRow, fetchData]);

    useEffect(() => {
        if (!gridRef.current) return;
    
        const api = gridRef.current.api;
        const updateFilter = async(api) => {
            if (spiderFilter.length === 0){
                await api.setColumnFilterModel('spider_name', null);
            } else {
                await api.setColumnFilterModel('spider_name', {
                    filterType: 'text',
                    type: 'contains',
                    filter: spiderFilter
                });
            }
            await api.onFilterChanged();
        };
        updateFilter(api);
      }, [spiderFilter]);

    let flattenedData;
    if (spiderLibraryData){
        const customOrder = ['vertical', 'project', 'spider_name'];
        
        // Function to reorder object fields
        const reorderObject = (obj) => {
            const ordered = customOrder.reduce((acc, key) => {
            if (key in obj) {
                acc[key] = obj[key];
            }
            return acc;
        }, {});
        
            Object.keys(obj).forEach((key) => {
                if (!customOrder.includes(key)) {
                    ordered[key] = obj[key];
                }
            });
            
            return ordered;
        };
        flattenedData = spiderLibraryData.map(obj =>
            Object.fromEntries(
                Object.entries(obj).map(([key, value]) => [
                    key,
                    Array.isArray(value) ? value.join(", ") : value
                ])
            )
        ).map(reorderObject).map((obj) => {
            return {
                ...obj,
                detailed: <Button size="sm" variant="success" onClick={() => {
                    setShowDetailedModal(true);
                    setDetailedObj(obj);
                }}>View Detailed</Button>
            }
        });
    }

    const submitSchedule = async() => {
        setLoading(true);
        setShowDetailedModal(false);
        try {
            const postData =scheduleObj?.spiderArgs.map((args) => (
                {
                    spider_key: detailedObj?.spider_key,
                    spider_name: detailedObj?.spider_name,
                    client: scheduleObj?.client,
                    spider_args_raw: {...args},
                    time: `${scheduleObj?.hour.split(":")[0]}:${scheduleObj?.minute}`,
                    day_of_week: scheduleObj?.dayOfWeek.map((day) => day.value === "Every day" ? null : day?.value.slice(0,3)).join(","),
                    day: scheduleObj?.day.map((day) => day.value === "Every day" ? null : day?.value).join(","),
                    batch_size: scheduleObj?.batch_size,
                    batch_time: scheduleObj?.batch_time,
                    tag: scheduleObj?.tag,
                    arg_table: scheduleObj?.arg_table,
                    schedule_id: null
                }
            ));
            await updatePortalSchedule(postData);
            setError();
            setAlert("Job Scheduled Successfully")
        } catch (e) {
            console.error(e);
            setError(e);
            setAlert("Error in scheduling job");
        } finally {
            setShowAlert(true);
            setLoading(false);
        }

    };


    return (
        <div>
            {/* Detailed Modal */}
            <Modal size="xl" show={showDetailedModal}>
                <Modal.Header closeButton onClick={() => setShowDetailedModal(false)}>
                    <Modal.Title>Job {detailedObj?.spider_key}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.SpiderLibraryModalBody}>
                        <div className={styles.SpiderLibraryButtonSpacing}>
                            <Button variant={detailedPage === "detailed" ? "dark" : "outline-dark" } onClick={() => setDetailedPage("detailed")}>Detailed View</Button>
                            <Button variant={detailedPage === "add-schedule" ? "dark" : "outline-dark" } onClick={() => setDetailedPage("add-schedule")}>Add Schedule</Button>
                        </div>
                        {detailedObj && detailedPage==="detailed" && 
                            Object?.keys(detailedObj).map((key) => {
                                return <span><strong>{toTitleCase(key?.replace(/_/g, " "))}:</strong> {detailedObj[key]}</span>
                        })}
                        {scheduleObj && detailedPage==="add-schedule" &&
                        <div>

                            <SpiderScheduleForm spiderName={detailedObj?.spider_name} 
                                scheduleObj={scheduleObj} 
                                setScheduleObj={setScheduleObj}
                                source="SpiderLibrary"
                            />
                                                
                        </div>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailedModal(false)}>
                        Close
                    </Button>
                    {detailedPage === "detailed" && <Link 
                        to="/spider-scheduling"
                        state={{spiderName: detailedObj?.spider_name}}>

                        <Button variant="success" onClick={() => 
                            {
                                setShowDetailedModal(false);
                            }}>
                            Go to Scheduling
                        </Button>
                    </Link>}
                    {detailedPage === "add-schedule" && 
                        // <Button variant="success" disabled={hasDuplicateKeys(scheduleObj.spiderArgs)} onClick={submitSchedule}>
                        <Button variant="success" onClick={submitSchedule}>
                            Add New Schedule
                        </Button>
                    }
                </Modal.Footer>
            </Modal>

            {alert && 
                <TimedAlert
                    message={alert}
                    duration={2000}
                    variant={error ? "danger" : "success"}
                    show={showAlert}
                    onClose={() => setShowAlert(false)}

              />
            }
            <div className={props?.parent === "Dashboard" ? styles.SpiderLibraryDashboard : styles.SpiderLibrary}>
                {location?.pathname === "/spider-library" ? <h4>Spider Library</h4> : <a href="/spider-library"><h4>Spider Library</h4> </a>}
                <br/>
                <div>
                    {/* <p>Filter by Spider Name</p> */}
                    <Form.Control type="text" placeholder="Filter by Spider Name" value={spiderFilter} onChange={(e) => setSpiderFilter(e.target.value)}/>
                </div>
                <br/>
                {(!loading && flattenedData) && 
                    <AGTable data={flattenedData} 
                    gridRef={gridRef}
                    columnsToHide={["barcode_example", "barcode_percentage", "barcode_valid", "barcode_scrape_date", "matching_checked", "spider_args"]} 
                    fileName="spider-library.csv"
                    editableFields={["barcode_checked", "matching_checked", "bedrock_status"]}
                    editableSelectConfig={[{
                        columnName: 'bedrock_status',
                        values: ['Bedrock', 'Both', 'Legacy']
                    }]}
                    setUpdatedRow={setUpdatedRow}
                    showSelect={true}
                    />
                }

                {loading && <div className={styles.spinnerContainer}>
                    <Spinner/>
                </div>}
            </div>
        </div>
    )
};

export default SpiderLibrary;